<script lang="ts" setup>
import { ref, onMounted, computed } from 'vue';
import { TechDataVehicle, Translations } from '../types/types';
import { getDefaultLocaleValue } from '../locales/default';
import { useTracking } from '@/composables/useTracking';
import { useIntersectionObserverOnce } from '@/composables/useIntersectionObserverOnce';

const props = defineProps<{
  vehicle: TechDataVehicle;
  translations: Translations;
  modelSeries: string;
}>();

const sliderContainer = ref<HTMLElement | null>(null);
const sliderComponent = ref<HTMLElement | null>(null);
const isVisible = ref<boolean>(false);
const currentSlide = ref<number>(1);
const visibleSlidesCount = ref<number>(1); // Default to 1 for mobile
const imagesLoaded = ref(0);
const hasInteracted = ref(false);
const initialSlide = ref<number | null>(null);

const getAltText = (vehicleClass: string, bodyType: string, key: string) => {
  let bodyTypeTranslated =
    props.translations[`owvc_tech_data_bodytype_${bodyType}`];
  let dimensions = props.translations[`owvc_tech_data_alt_image_${key}`];

  if (bodyTypeTranslated && dimensions) {
    return `Mercedes-Benz ${vehicleClass} ${bodyTypeTranslated.toLowerCase()}: ${dimensions}`;
  } else {
    return `Mercedes-Benz ${vehicleClass} ${bodyType}: ${getDefaultLocaleValue(
      `alt_image_${key}`,
    )}`;
  }
};

const updateCurrentSlide = async () => {
  if (sliderComponent.value) {
    const slider = sliderComponent.value as any;
    if (slider.getCurrentItem) {
      currentSlide.value = await slider.getCurrentItem();
    }
  }
};

const realSlidesCount = computed(
  () => Object.keys(props.vehicle.imagesData).length,
);

const onImageLoad = () => {
  imagesLoaded.value += 1;
};

const handleSliderChange = async () => {
  await updateCurrentSlide();
  // Set initialSlide on first wbchange
  if (initialSlide.value === null) {
    initialSlide.value = currentSlide.value;
    return;
  }
  // Only set hasInteracted if slide actually changed
  if (!hasInteracted.value && currentSlide.value !== initialSlide.value) {
    hasInteracted.value = true;
  }
};

onMounted(() => {
  updateCurrentSlide();
});

useIntersectionObserverOnce(
  sliderContainer,
  {
    rootMargin: '200px 0px',
    threshold: 0,
  },
  () => {
    isVisible.value = true;
  },
);
</script>

<template>
  <div class="owvans-tech-data__slider" ref="sliderContainer">
    <div class="wb-grid-container" v-if="!isVisible">
      <div class="tech-data-slider-ssr">
        <button
          class="tech-data-slider-ssr__arrow-button"
          aria-label="Next image"
          v-if="Object.keys(props.vehicle.imagesData).length > 2"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path
              d="M5.35156 20.7953L16.3828 9.76252L18.5906 11.9688L7.55781 23L5.35156 20.7953Z"
            ></path>
            <path
              d="M5.41394 3.20469L7.61863 1L18.6514 12.0328L16.4452 14.239L5.41394 3.20469Z"
            ></path>
          </svg>
        </button>
        <div class="tech-data-slider-ssr__images-container">
          <div
            class="tech-data-slider-ssr__image"
            v-for="(variation, key) in props.vehicle.imagesData"
            :key="key"
          >
            <img
              :alt="getAltText(vehicle.vehicleClass, vehicle.bodyType, key)"
              :src="variation"
              @load="onImageLoad"
            />
          </div>
        </div>
      </div>
    </div>
    <wb7-slider
      v-else
      ref="sliderComponent"
      class="wb-slider hydrated"
      grid-gutter=""
      navigation-behaviour="page"
      show-arrow-navigation="true"
      off-grid
      scroll-snap=""
      @wbchange="handleSliderChange"
      next-button-aria-label="Next image"
      previous-button-aria-label="Previous image"
    >
      <wb7-slider-item
        class="wb-slider-item hydrated"
        v-for="(variation, key) in props.vehicle.imagesData"
        :key="key"
        mq1="11"
        mq3="6"
      >
        <img
          :src="variation"
          :alt="getAltText(vehicle.vehicleClass, vehicle.bodyType, key)"
        />
      </wb7-slider-item>
      <div
        v-if="realSlidesCount > visibleSlidesCount"
        class="wb-slider-item__spacer"
        aria-hidden="true"
      />
    </wb7-slider>
  </div>
</template>

<style lang="scss" scoped>
$arrow-btn-size: 48px;
$arrow-btn-margin: 16px;
$arrow-btn-bg: #ffffff;
$arrow-btn-radius: 50%;

.tech-data-slider-ssr {
  position: relative;

  &__arrow-button {
    position: absolute;
    top: 50%;
    right: -4%;
    width: $arrow-btn-size;
    height: $arrow-btn-size;
    margin-right: $arrow-btn-margin;
    background-color: $arrow-btn-bg !important;
    border: var(--round-button-border-weight) solid;
    transform: translateY(-50%);
    box-shadow: var(--wb-shadow-s);
    border-radius: $arrow-btn-radius;
    cursor: pointer;
    z-index: 10;

    svg {
      width: 16px;
    }
  }

  &__images-container {
    height: 100%;
    display: flex;
    gap: var(--wb-grid-gutter-width);
    flex-wrap: nowrap;
  }

  &__image {
    background: var(--wb-grey-95);
    height: 100%;
    flex-shrink: 0;
    opacity: 0.5;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }
}

// Mobile (1.33 slides)
@media (max-width: 767px) {
  .tech-data-slider-ssr {
    overflow: hidden;
  }
  .tech-data-slider-ssr__image {
    width: calc(100% / 1.21);
    &:first-child {
      opacity: 1;
    }
  }
  .tech-data-slider-ssr__arrow-button {
    display: none;
  }
}

// Desktop (2.4 slides)
@media (min-width: 768px) {
  .tech-data-slider-ssr__image {
    width: calc(100% / 2.05);
    &:nth-child(-n + 2) {
      opacity: 1;
    }
    &:nth-child(n + 4) {
      display: none;
    }
  }
}

.wb-slider-item {
  background: var(--wb-grey-95);
  // Opacity change for inactive images are removed for better visibility
  // but I kept the feature here in case we want to reintroduce it.
  // in case we want to reintroduce it set the opacity to 0.4
  opacity: 1;

  &.__active {
    opacity: 1;
  }

  &__spacer {
    // Calculated width based on workbench grid
    width: calc((100vw - min(86vw, 105rem)) / 2);
    flex-shrink: 0;
    margin: 0;
    background: transparent;
    pointer-events: none;
  }
}
</style>
